import {
  Container,
  ContainerGutter,
  ContainerPadding,
  CTA,
  Text,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useFeatureServices } from '../../use-feature-services';
import { QuestionsQuiz } from './questionsQuiz';
import { useNavigate } from 'react-router-dom';
import { FaContentGTI } from '../../home';

const BackgroundGtiColor = styled.section`
  width: 100%;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  position: relative;

  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/textura-negra2?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD13ZWJwJnFsdD03OSZiZmM9b2ZmJmJmMTc=');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  overflow: hidden;
`;

const PrimaryText = styled(Text)`
  font-weight: 800;
  font-style: ExtraBold;
  leading-trim: NONE;
  line-height: 90%;
  letter-spacing: 0%;
  text-align: left;
  color: #ffffff;
  font-size: clamp(26px, 5vw, 64px);
`;

const SecondaryText = styled(Text)`
  font-weight: 400;
  font-style: Regular;
  leading-trim: NONE;
  line-height: 90%;
  letter-spacing: 0%;
  text-align: left;
  color: #ffffff;
  font-size: clamp(20px, 3vw, 32px);
`;

const TopLeftText = styled.div`
  position: absolute;
  top: 10%;
  left: 5%;
  color: #ffffff;
  z-index: 1;
`;

const StyledCTA = styled(CTA)`
  background-color: transparent !important;
  border: 1px solid #ffffff !important;
  color: #ffffff !important;
  will-change: transform, background-color, color, border-color;
  transform-origin: center center;
  transition: all 0.3s ease;
  padding: 8px 16px !important;
  font-size: 14px !important;

  @media (max-width: 768px) {
    padding: 6px 12px !important;
    font-size: 12px !important;
  }

  &:hover {
    background-color: #c90010 !important;
    border-color: #c90010 !important;
    color: #ffffff !important;
  }

  &:active {
    background-color: #c90010 !important;
    border-color: #c90010 !important;
    color: #ffffff !important;
  }
`;

const StyledButton = styled(CTA)`
  background-color: #c90010;
  color: #ffffff;
  will-change: transform, background-color, color;
  transform-origin: center center;
  margin-left: auto;
  cursor: pointer;

  :hover,
  :active,
  :focus,
  :focus-visible {
    background: #8a010cff !important;
    color: #ffffff !important;
  }
`;

const WhiteText = styled(Text)`
  color: #ffffff !important;
`;

const LeftContent = styled(Container)`
  padding-top: 22vh;
`;

const TransitionContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
`;

const ComponentWrapper = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  transition: opacity 0.5s ease;
`;

const MovingImage = styled.img`
  position: absolute;
  bottom: 0%;
  height: 35dvh;
  width: auto;
  animation: moveRightToLeft 18s linear -6s forwards;
  z-index: 0;

  @media (max-width: 768px) {
    animation: moveRightToLeft 15s linear -6s forwards;
  }

  @media (min-width: 1200px) {
    animation: moveRightToLeft 22s linear -8s forwards;
  }

  @keyframes moveRightToLeft {
    0% {
      transform: translateX(calc(100vw + 100%));
    }
    100% {
      transform: translateX(calc(-100vw - 100%));
    }
  }
`;

export const IntroQuizGti50 = (): React.ReactElement => {
  const {
    'breadcrumb-service': breacrumbService,
    'page-info-service': pageInfoService,
    'fa-content-service': contentService,
  } = useFeatureServices();
  const navigate = useNavigate();
  const faContentGTI = contentService?.content as FaContentGTI;

  const [showQuestions, setShowQuestions] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1260 : false);

  React.useEffect(() => {
    breacrumbService.set([]);
    pageInfoService.setPageInfo();
  }, [pageInfoService, breacrumbService]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1260);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <TransitionContainer>
      <ComponentWrapper isVisible={!showQuestions}>
        <BackgroundGtiColor>
          {faContentGTI.active_gti_form == 'true' && (
            <TopLeftText>
              <StyledCTA
                tag="button"
                emphasis="secondary"
                onClick={() => {
                  navigate('/');
                }}
                aria-label="aria label for CTA"
              >
                <WhiteText appearance={TokenTextAppearance.copy100} bold>
                  Descubrir Golf GTI
                </WhiteText>
              </StyledCTA>
            </TopLeftText>
          )}

          <LeftContent
            horizontalAlign={'flex-start'}
            wrap={isDesktop ? 'never' : 'always'}
            padding={{
              left: isDesktop ? ContainerPadding.grid004 : ContainerPadding.grid002,
              right: ContainerPadding.grid001,
            }}
          >
            {isDesktop ? (
              <Container wrap={'never'}>
                <Container wrap={'always'} horizontalAlign={'flex-start'}>
                  <SecondaryText>El carácter GTI</SecondaryText>
                  <PrimaryText>o se tiene</PrimaryText>
                  <PrimaryText>o no se tiene</PrimaryText>
                </Container>
                <Container
                  wrap={'always'}
                  padding={{ left: ContainerPadding.grid004 }}
                  gutter={ContainerGutter.dynamic0130}
                >
                  <Container wrap={'always'} gutter={ContainerGutter.static100}>
                    <SecondaryText>
                      Y para conducir el <b>Nuevo Golf GTI 50</b>
                    </SecondaryText>
                    <SecondaryText>
                      <b>Aniversario</b> solo existe una opción
                    </SecondaryText>
                  </Container>
                  <StyledButton
                    tag="button"
                    emphasis="primary"
                    onClick={() => {
                      setShowQuestions(true);
                    }}
                    aria-label="aria label for CTA"
                  >
                    <Text appearance={TokenTextAppearance.headline100} bold>
                      ¡Ponte a prueba!
                    </Text>
                  </StyledButton>
                </Container>
              </Container>
            ) : (
              <Container wrap={'always'} gutter={ContainerGutter.static450}>
                <Container wrap={'always'} horizontalAlign={'flex-start'}>
                  <SecondaryText>El carácter GTI</SecondaryText>
                  <PrimaryText>o se tiene</PrimaryText>
                  <PrimaryText>o no se tiene</PrimaryText>
                </Container>
                <Container wrap={'always'} gutter={ContainerGutter.static100}>
                  <SecondaryText>
                    Y para conducir el <b>Nuevo Golf GTI 50</b>
                  </SecondaryText>
                  <SecondaryText>
                    <b>Aniversario</b> solo existe una opción
                  </SecondaryText>
                </Container>
                <StyledButton
                  tag="button"
                  emphasis="primary"
                  onClick={() => {
                    setShowQuestions(true);
                  }}
                  aria-label="aria label for CTA"
                >
                  <Text appearance={TokenTextAppearance.headline100} bold>
                    ¡Ponte a prueba!
                  </Text>
                </StyledButton>
              </Container>
            )}
          </LeftContent>
          <MovingImage
            src="https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/formLeads/Son%CC%83ar-en-rojo.png"
            alt="Moving Text"
          />
        </BackgroundGtiColor>
      </ComponentWrapper>

      <ComponentWrapper isVisible={showQuestions}>
        <QuestionsQuiz />
      </ComponentWrapper>
    </TransitionContainer>
  );
};
